import React, { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import Box from '@mui/material/Box';
import Card from '@mui/material/Card';
import CardContent from '@mui/material/CardContent';
import Typography from '@mui/material/Typography';
import TextField from '@mui/material/TextField';
import Button from '@mui/material/Button';
import Alert from '@mui/material/Alert';
import CircularProgress from '@mui/material/CircularProgress';
import Link from '@mui/material/Link';
import useAuth from '../hooks/useAuth';

// Modes: 'login' | 'forgot_email' | 'forgot_code' | 'forgot_done'
export default function LoginPage() {
  const { login, forgotPassword, confirmForgotPassword } = useAuth();
  const navigate = useNavigate();

  const [mode, setMode] = useState('login');
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [newPassword, setNewPassword] = useState('');
  const [showNewPassword, setShowNewPassword] = useState(false);
  const [code, setCode] = useState('');
  const [resetPassword, setResetPassword] = useState('');
  const [error, setError] = useState('');
  const [info, setInfo] = useState('');
  const [loading, setLoading] = useState(false);

  const resetForm = () => {
    setError('');
    setInfo('');
    setPassword('');
    setNewPassword('');
    setShowNewPassword(false);
    setCode('');
    setResetPassword('');
  };

  const handleLogin = async (e) => {
    e.preventDefault();
    setError('');
    setLoading(true);
    try {
      const result = await login(email, password, showNewPassword ? newPassword : undefined);
      if (result?.challengeName === 'FORCE_CHANGE_PASSWORD') {
        setShowNewPassword(true);
        setLoading(false);
        return;
      }
      navigate('/', { replace: true });
    } catch (err) {
      setError(err.message || 'Login failed. Please check your credentials.');
    } finally {
      setLoading(false);
    }
  };

  const handleForgotRequest = async (e) => {
    e.preventDefault();
    setError('');
    setLoading(true);
    try {
      await forgotPassword(email);
      setMode('forgot_code');
      setInfo('We sent a verification code to your email. Enter it below along with your new password.');
    } catch (err) {
      setError(err.message || 'Failed to send reset code.');
    } finally {
      setLoading(false);
    }
  };

  const handleForgotConfirm = async (e) => {
    e.preventDefault();
    setError('');
    setLoading(true);
    try {
      await confirmForgotPassword(email, code, resetPassword);
      setMode('forgot_done');
      setInfo('Password reset successfully. Sign in with your new password.');
    } catch (err) {
      setError(err.message || 'Failed to reset password. Code may be expired.');
    } finally {
      setLoading(false);
    }
  };

  return (
    <Box
      sx={{
        minHeight: '100vh',
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        background: 'linear-gradient(135deg, #1e3a8a 0%, #7c3aed 100%)',
        p: 2,
      }}
    >
      <Card sx={{ maxWidth: 420, width: '100%' }}>
        <CardContent sx={{ p: 4 }}>
          <Box sx={{ textAlign: 'center', mb: 3 }}>
            <Box component="img" src="/images/cwc-logo-full.png" alt="Cyber Wise Corp"
              sx={{ height: 100, objectFit: 'contain', borderRadius: 1, mb: 1 }} />
            <Typography variant="body2" color="text.secondary">
              Intern Portal
            </Typography>
          </Box>

          {error && <Alert severity="error" sx={{ mb: 2 }}>{error}</Alert>}
          {info && <Alert severity="info" sx={{ mb: 2 }}>{info}</Alert>}

          {/* LOGIN MODE */}
          {mode === 'login' && (
            <form onSubmit={handleLogin}>
              <TextField
                label="Email"
                type="email"
                value={email}
                onChange={(e) => setEmail(e.target.value)}
                fullWidth
                required
                margin="normal"
                autoComplete="email"
                autoFocus
              />
              <TextField
                label="Password"
                type="password"
                value={password}
                onChange={(e) => setPassword(e.target.value)}
                fullWidth
                required
                margin="normal"
                autoComplete="current-password"
              />
              {showNewPassword && (
                <>
                  <Alert severity="info" sx={{ mt: 1, mb: 1 }}>
                    You must set a new password to continue.
                  </Alert>
                  <TextField
                    label="New Password"
                    type="password"
                    value={newPassword}
                    onChange={(e) => setNewPassword(e.target.value)}
                    fullWidth
                    required
                    margin="normal"
                    autoComplete="new-password"
                    helperText="Min 8 chars, must include uppercase, lowercase, number, and special character"
                  />
                </>
              )}
              <Button type="submit" variant="contained" fullWidth size="large" disabled={loading} sx={{ mt: 2, py: 1.5 }}>
                {loading ? <CircularProgress size={24} color="inherit" /> :
                  showNewPassword ? 'Set New Password & Login' : 'Sign In'}
              </Button>

              <Box sx={{ mt: 2, textAlign: 'center' }}>
                <Link
                  component="button"
                  type="button"
                  variant="body2"
                  onClick={() => { setMode('forgot_email'); resetForm(); }}
                  sx={{ color: 'primary.main' }}
                >
                  Forgot password?
                </Link>
              </Box>
            </form>
          )}

          {/* FORGOT PASSWORD - REQUEST CODE */}
          {mode === 'forgot_email' && (
            <form onSubmit={handleForgotRequest}>
              <Typography variant="body2" sx={{ mb: 2 }}>
                Enter your email and we'll send a verification code to reset your password.
              </Typography>
              <TextField
                label="Email"
                type="email"
                value={email}
                onChange={(e) => setEmail(e.target.value)}
                fullWidth
                required
                margin="normal"
                autoComplete="email"
                autoFocus
              />
              <Button type="submit" variant="contained" fullWidth size="large" disabled={loading} sx={{ mt: 2, py: 1.5 }}>
                {loading ? <CircularProgress size={24} color="inherit" /> : 'Send Reset Code'}
              </Button>
              <Box sx={{ mt: 2, textAlign: 'center' }}>
                <Link
                  component="button"
                  type="button"
                  variant="body2"
                  onClick={() => { setMode('login'); resetForm(); }}
                  sx={{ color: 'text.secondary' }}
                >
                  Back to sign in
                </Link>
              </Box>
            </form>
          )}

          {/* FORGOT PASSWORD - CONFIRM CODE + NEW PASSWORD */}
          {mode === 'forgot_code' && (
            <form onSubmit={handleForgotConfirm}>
              <TextField
                label="Verification Code"
                value={code}
                onChange={(e) => setCode(e.target.value)}
                fullWidth
                required
                margin="normal"
                autoComplete="one-time-code"
                autoFocus
              />
              <TextField
                label="New Password"
                type="password"
                value={resetPassword}
                onChange={(e) => setResetPassword(e.target.value)}
                fullWidth
                required
                margin="normal"
                autoComplete="new-password"
                helperText="Min 8 chars, must include uppercase, lowercase, number, and special character"
              />
              <Button type="submit" variant="contained" fullWidth size="large" disabled={loading} sx={{ mt: 2, py: 1.5 }}>
                {loading ? <CircularProgress size={24} color="inherit" /> : 'Reset Password'}
              </Button>
              <Box sx={{ mt: 2, textAlign: 'center' }}>
                <Link
                  component="button"
                  type="button"
                  variant="body2"
                  onClick={() => { setMode('login'); resetForm(); }}
                  sx={{ color: 'text.secondary' }}
                >
                  Back to sign in
                </Link>
              </Box>
            </form>
          )}

          {/* FORGOT PASSWORD - DONE */}
          {mode === 'forgot_done' && (
            <Box>
              <Button
                variant="contained"
                fullWidth
                size="large"
                onClick={() => { setMode('login'); resetForm(); }}
                sx={{ mt: 2, py: 1.5 }}
              >
                Sign In Now
              </Button>
            </Box>
          )}

          <Box sx={{ mt: 3, textAlign: 'center' }}>
            <Typography variant="caption" color="text.secondary">
              Not an intern yet?{' '}
              <a href="https://students.cyberwisecorp.com" style={{ color: '#7c3aed' }}>
                Apply here
              </a>
            </Typography>
          </Box>
        </CardContent>
      </Card>
    </Box>
  );
}
